import { useState } from 'react'
import { Botao } from '../../../components/Botao'
import { Cartao } from '../../../components/ListaDeDados'
import { TEXTOS } from '../../../lib/glossario'
import type { ParametroContratual } from '../../../types/chamado'

/** O selo do critério, na cor que o público lê sem legenda. */
const COR_DO_STATUS: Record<ParametroContratual['status'], string> = {
  FORMALIZADO: 'bg-green-100 text-green-900',
  PROVISORIO: 'bg-amber-100 text-amber-900',
  INDETERMINADO: 'bg-slate-200 text-slate-800',
}

/**
 * Com que regra este mês foi apurado.
 *
 * **O Termo de Referência se contradiz em doze pontos, e cada contradição decide dinheiro.** Ler o
 * item 8.13 (duas casas sem arredondar) ou a observação "arredondar sempre o %" muda um ponto
 * percentual de glosa na mesma célula — e o próprio TR pratica os dois modos, em páginas
 * diferentes. Enquanto essas escolhas viviam em constante de código, três perguntas não tinham
 * resposta: com que regra, quem decidiu, e de onde no TR isso sai.
 *
 * **O selo é a informação principal.** "Provisório" diz à contratada que o critério ainda é
 * discutível e que a Prefeitura pode formalizá-lo de outro jeito; "indeterminado" diz que o
 * indicador correspondente não é apurado de propósito. Esconder isso faria o número parecer mais
 * assentado do que é.
 *
 * Fica recolhido por padrão: quem abre o fechamento quer os indicadores, e vai aos critérios
 * quando discorda de um deles.
 *
 * @param props os critérios vigentes ou carimbados, e se a competência já fechou
 * @returns o cartão dos critérios
 */
export function CriteriosDoTr({
  parametros,
  fechada,
}: {
  parametros: ParametroContratual[]
  fechada: boolean
}) {
  const [aberto, setAberto] = useState(false)

  if (parametros.length === 0) {
    return null
  }

  const indeterminados = parametros.filter((p) => p.status === 'INDETERMINADO').length
  const provisorios = parametros.filter((p) => p.status === 'PROVISORIO').length

  return (
    <Cartao
      titulo={TEXTOS.criterios.titulo}
      descricao={fechada ? TEXTOS.criterios.carimbados : TEXTOS.criterios.vigentes}
      acao={
        <Botao tamanho="pequeno" onClick={() => setAberto(!aberto)}>
          {aberto ? TEXTOS.criterios.recolher : TEXTOS.criterios.abrir(parametros.length)}
        </Botao>
      }
    >
      <p className="max-w-prose text-sm text-slate-600">
        {TEXTOS.criterios.resumo(provisorios, indeterminados)}
      </p>

      {aberto && (
        <ul className="mt-4 space-y-4">
          {parametros.map((p) => (
            <li key={p.chave} className="border-t border-slate-100 pt-3 first:border-0 first:pt-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-xs text-slate-500">{p.chave}</span>
                <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${COR_DO_STATUS[p.status]}`}>
                  {TEXTOS.criterios.status[p.status]}
                </span>
                <span className="text-xs text-slate-400">versão {p.versao}</span>
              </div>
              <p className="mt-1 max-w-prose text-sm text-slate-700">{p.fundamento}</p>
              <p className="mt-1 text-xs text-slate-500">
                {TEXTOS.criterios.fonte}: {p.fonteTr}
              </p>
              {p.riscoResidual && (
                <p className="mt-1 max-w-prose text-xs text-amber-800">
                  {TEXTOS.criterios.risco}: {p.riscoResidual}
                </p>
              )}
              {p.atoFormal && (
                <p className="mt-1 text-xs text-green-800">
                  {TEXTOS.criterios.ato}: {p.atoFormal}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </Cartao>
  )
}
